import type { HsClippingPlane } from '../../../engine-new/objects/clipping/clipping-plane.class';
import { HsClippingStorage } from '../../../engine-new/storages/clipping-storage.class';
import { HsLightStorage } from '../../../engine-new/storages/light-storage.class';
import { HsShader } from './shader.class';
import type { HsLight } from '../../../engine-new/objects/lights/light.class';
import { HsShaderLightFunctions } from '../functions/shader-light-functions.function';

export type HsMeshBatchMode = 'instances' | 'multi-draw';

export class HsDefaultShader extends HsShader {
  public lightStorage: HsLightStorage = HsLightStorage.default;
  public clippingStorage: HsClippingStorage = HsClippingStorage.default;
  private lightParams: Float32Array = new Float32Array(0);
  private lightColors: Float32Array = new Float32Array(0);
  private meshBatchFactory: ((mode: HsMeshBatchMode) => HsDefaultShader) | null = null;
  private meshBatchVertexCode: () => string = this.makeVertexCode;
  private meshBatchFragmentCode: () => string = this.makeFragmentCode;

  public get makeMeshBatchShader(): ((mode: HsMeshBatchMode) => HsDefaultShader) | null {
    return this.makeVertexCode === this.meshBatchVertexCode && this.makeFragmentCode === this.meshBatchFragmentCode
      ? this.meshBatchFactory
      : null;
  }

  public set makeMeshBatchShader(factory: ((mode: HsMeshBatchMode) => HsDefaultShader) | null) {
    this.meshBatchFactory = factory;
    this.meshBatchVertexCode = this.makeVertexCode;
    this.meshBatchFragmentCode = this.makeFragmentCode;
  }

  /** The renderer selects each program at least once per frame, so moved lights and planes take effect there. */
  public override useProgram(gl: WebGL2RenderingContext): void {
    super.useProgram(gl);
    this.uploadDynamicLights(gl);
    this.uploadClippingPlanes(gl);
  }

  public enable(gl: WebGL2RenderingContext): void {
    super.enable(gl);
    this.uploadClippingPlanes(gl);
  }

  private uploadDynamicLights(gl: WebGL2RenderingContext): void {
    const params: WebGLUniformLocation | undefined = this.uniforms.get('u_lightParams[0]');
    const colors: WebGLUniformLocation | undefined = this.uniforms.get('u_lightColors[0]');
    if (params === undefined && colors === undefined) {
      return;
    }
    const lights: HsLight[] = this.lightStorage.lights;
    const slots: { transforms: number; colors: number } = HsShaderLightFunctions.countDynamicSlots(lights);
    if (this.lightParams.length !== slots.transforms * 3) {
      this.lightParams = new Float32Array(slots.transforms * 3);
    }
    if (this.lightColors.length !== slots.colors * 4) {
      this.lightColors = new Float32Array(slots.colors * 4);
    }
    HsShaderLightFunctions.writeDynamicValues(lights, this.lightParams, this.lightColors);
    if (params !== undefined && this.lightParams.length > 0) {
      gl.uniform3fv(params, this.lightParams);
    }
    if (colors !== undefined && this.lightColors.length > 0) {
      gl.uniform4fv(colors, this.lightColors);
    }
  }

  private uploadClippingPlanes(gl: WebGL2RenderingContext): void {
    if (!this.uniforms.has('u_clippingPlanes[0]')) {
      return;
    }
    const clippingStorage: HsClippingStorage = this.clippingStorage;
    const clippingPlanes: HsClippingPlane[] = clippingStorage.clippingPlanes;
    const planesData: number[] = [];
    for (let i: number = 0; i < clippingPlanes.length; i++) {
      const plane: HsClippingPlane = clippingPlanes[i];
      planesData.push(plane.normal.x, plane.normal.y, plane.normal.z, plane.offset);
    }
    this.uniform4fv(gl, 'u_clippingPlanes[0]', new Float32Array(planesData));
  }
}
